import React from 'react';

export interface TemplateStringProps {
  templateString: string;
  componentMap: Record<string, React.ComponentType>;
}

/**
 * Renders a string with embedded React components.
 * The string can contain placeholders in the format <ComponentName/>.
 * The componentMap prop maps component names to actual React components.
 *
 * @example
 * <TemplateString
 *   templateString="Hello, <Bold/>! Welcome to <Italic/>."
 *   componentMap={{ Bold: () => <strong>World</strong>, Italic: () => <em>React</em> }}
 * />
 *
 * This will render: Hello, <strong>World</strong>! Welcome to <em>React</em>.
 *
 * Useful for i18n where translations may need  to include react components.
 */
export function TemplateString({
  templateString,
  componentMap,
}: TemplateStringProps) {
  // Split the string by component placeholders while capturing the component names
  const regex = /<(.+?)\/>/;
  const parts: (string | React.ReactElement)[] = [];

  let remainingString = templateString;
  let key = 0;

  while (remainingString.length > 0) {
    const match = remainingString.match(regex);

    if (!match) {
      // No more components found, add the remaining text
      if (remainingString) {
        parts.push(remainingString);
      }
      break;
    }

    // Add text before the component
    const textBefore = remainingString.substring(0, match.index);
    if (textBefore) {
      parts.push(textBefore);
    }

    // Add the component
    const componentName = match[1];
    const Component = componentMap[componentName];

    if (Component) {
      parts.push(<Component key={key++} />);
    } else {
      // Fallback: render the placeholder as text if component not found
      parts.push(match[0]);
    }

    // Continue with the rest of the string
    remainingString = remainingString.substring(
      (match.index || 0) + match[0].length
    );
  }

  return <span className="whitespace-pre-line">{parts}</span>;
}
